import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'
import { PackageX, CheckCircle } from 'lucide-react'
import { publicApi } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'

// Configuração do mapa - mesmo padrão de RecipientForm.jsx/PinConfirmView.jsx
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
}

// Centro padrão: Manaus - mesmo fallback usado quando a geocodificação
// automática falha ou não encontra o endereço
const defaultCenter = {
  lat: -3.1190,
  lng: -60.0217
}

// Campos obrigatórios pra habilitar a busca no mapa - complemento fica de fora
const REQUIRED_FIELDS = ['street', 'number', 'neighborhood', 'city', 'state', 'zipcode']

// Página pública (sem login) que o destinatário abre a partir do link
// enviado pelo vendedor via WhatsApp, quando o cadastro foi criado com
// "Solicitar endereço do destinatário" marcado. Sem MainLayout/Sidebar de
// propósito - mesmo padrão do PinConfirmView/DriverLinkView: tela isolada,
// pensada pra abrir direto no navegador do celular. Sem react-hook-form -
// state local simples, mesma escolha do PinConfirmView pra esse tipo de tela
// pública leve.
export default function AddressFillView() {
  const { token } = useParams()
  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: '',
  })
  const [coordinates, setCoordinates] = useState(null)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [geocodeWarning, setGeocodeWarning] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [mutationFailed, setMutationFailed] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['addressRequest', token],
    queryFn: async () => {
      const response = await publicApi.get(`/api/address-request/${token}`)
      return response.data.data
    },
    retry: false,
  })

  const submitMutation = useMutation({
    mutationFn: () => publicApi.post(`/api/address-request/${token}`, {
      address: {
        street: address.street,
        number: address.number,
        complement: address.complement || null,
        neighborhood: address.neighborhood,
        city: address.city,
        state: address.state,
        zipcode: address.zipcode,
      },
      lat: coordinates.lat,
      lng: coordinates.lng,
    }),
    onSuccess: () => setSubmitted(true),
    onError: (error) => {
      // 404 = token morto (inválido, expirado ou já preenchido) - mesma tela
      // genérica de erro. Qualquer outro status (400 de validação) fica na
      // própria tela, mostrando o que reprovou.
      if (error.response?.status === 404) {
        setMutationFailed(true)
        return
      }

      const details = error.response?.data?.details
      setSubmitError(
        details && details.length > 0
          ? details.map((d) => d.message)
          : ['Não foi possível salvar o endereço. Confira os campos e tente novamente.']
      )
    },
  })

  const handleFieldChange = (field) => (e) => {
    setAddress((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const canGeocode = REQUIRED_FIELDS.every((field) => address[field].trim() !== '')

  // Geocodifica no backend (GET /api/address-request/:token/geocode), não
  // no browser: a chave do frontend (VITE_GOOGLE_MAPS_API_KEY) não tem a
  // Geocoding API autorizada de propósito - só Maps JavaScript API, pra
  // renderizar o mapa. O endpoint fica atrás do mesmo token público.
  const handleGeocode = async () => {
    setIsGeocoding(true)
    setGeocodeWarning(null)

    try {
      const response = await publicApi.get(`/api/address-request/${token}/geocode`, {
        params: {
          street: address.street,
          number: address.number,
          neighborhood: address.neighborhood,
          city: address.city,
          state: address.state,
        }
      })

      if (response.data.data) {
        setCoordinates(response.data.data)
      } else {
        // Endereço não encontrado com confiança suficiente: não bloqueia o
        // fluxo, só cai no centro de Manaus pro destinatário ajustar o pino
        // manualmente.
        setCoordinates(defaultCenter)
        setGeocodeWarning('Não encontramos esse endereço automaticamente — ajuste o pino manualmente')
      }
    } catch (error) {
      // 404 = token morto de verdade (expirou ou foi preenchido em outra
      // aba enquanto essa ficou aberta) - diferente do 400 de validação do
      // submit, aqui não há formulário válido pra preservar.
      if (error.response?.status === 404) {
        setMutationFailed(true)
        return
      }

      // Qualquer outro erro (rede, 5xx): não dá pra distinguir com segurança
      // de endereço ruim, e matar o token por um erro transitório seria
      // pior que deixar tentar de novo - mesmo fallback de cima.
      setCoordinates(defaultCenter)
      setGeocodeWarning('Não encontramos esse endereço automaticamente — ajuste o pino manualmente')
    } finally {
      setIsGeocoding(false)
    }
  }

  const onMarkerDragEnd = (e) => {
    setCoordinates({ lat: e.latLng.lat(), lng: e.latLng.lng() })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Mesma regra do PinConfirmView: GET com erro OU mutation com erro caem na
  // mesma tela genérica - o backend não distingue token inválido, expirado
  // ou endereço já preenchido (sempre 404), e o front não tenta adivinhar.
  if (isError || !data || mutationFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <PackageX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Este link não está mais disponível</p>
          <p className="text-sm text-gray-500 mt-1">Fale com quem te enviou o link pra conseguir um novo.</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium">Endereço cadastrado, obrigado!</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <h1 className="text-lg font-semibold text-gray-900">Complete seu endereço</h1>
      </div>

      <div className="p-4 space-y-4">
        <Card padding="sm">
          <p className="text-sm text-gray-600">
            Olá, {data.name}! Precisamos do seu endereço para a entrega.
          </p>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="CEP"
            placeholder="69000-000"
            value={address.zipcode}
            onChange={handleFieldChange('zipcode')}
          />
          <div></div>

          <Input
            label="Rua"
            placeholder="Nome da rua"
            value={address.street}
            onChange={handleFieldChange('street')}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Número"
              placeholder="123"
              value={address.number}
              onChange={handleFieldChange('number')}
            />
            <Input
              label="Complemento"
              placeholder="Apto, etc."
              value={address.complement}
              onChange={handleFieldChange('complement')}
            />
          </div>

          <Input
            label="Bairro"
            placeholder="Nome do bairro"
            value={address.neighborhood}
            onChange={handleFieldChange('neighborhood')}
          />
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Input
                label="Cidade"
                placeholder="Manaus"
                value={address.city}
                onChange={handleFieldChange('city')}
              />
            </div>
            <Input
              label="UF"
              placeholder="AM"
              maxLength={2}
              value={address.state}
              onChange={handleFieldChange('state')}
            />
          </div>
        </div>

        <Button
          variant="secondary"
          fullWidth
          disabled={!canGeocode}
          isLoading={isGeocoding}
          onClick={handleGeocode}
        >
          Localizar endereço no mapa
        </Button>

        {geocodeWarning && (
          <p className="text-sm text-amber-600">{geocodeWarning}</p>
        )}

        {coordinates && (
          <>
            <p className="text-sm text-gray-600">
              Ajuste o pino pra sua localização exata, se precisar.
            </p>

            {isLoaded && (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={coordinates}
                zoom={17}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker position={coordinates} draggable={true} onDragEnd={onMarkerDragEnd} />
              </GoogleMap>
            )}
          </>
        )}
      </div>

      <div className="px-4 space-y-2">
        {submitError && (
          <div className="space-y-1">
            {submitError.map((message, i) => (
              <p key={i} className="text-sm text-red-600">{message}</p>
            ))}
          </div>
        )}

        <Button
          fullWidth
          disabled={!coordinates || !canGeocode}
          onClick={() => {
            setSubmitError(null)
            submitMutation.mutate()
          }}
          isLoading={submitMutation.isPending}
        >
          Confirmar endereço
        </Button>
      </div>
    </div>
  )
}
